import { useSetAtom } from "jotai";

import { useEffect, useRef } from "react";

import { authBootstrapStateAtom, currentUserAtom } from "../atoms/auth.atoms";

import authService from "../services/authService";

import { syncSessionWithExtension } from "../lib/syncSessionWithExtension";

/**
 * Runs once on app mount to determine whether the user is logged in.
 *
 * Works in both contexts:
 *   - Browser (app.eriol.io): the HttpOnly cookie set by /auth/login rides
 *     along on /me via credentials:include. 200 → authenticated.
 *   - Electron: the Authorization header is added by lib/authFetch from
 *     localStorage. Same /me path.
 *
 * A 401 just means "no session" — we transition to "unauthenticated" and
 * let route guards send the user to /login. Anything else (network error,
 * 5xx) also lands in "unauthenticated" since we have no proof of session;
 * the route guard handles that the same way (kicks to /login).
 *
 * The `auth-changed` window event (dispatched by Login/Signup hooks) lets
 * us re-bootstrap after a fresh login without a full page reload.
 */

export function useAuthBootstrap() {
  const setState = useSetAtom(authBootstrapStateAtom);

  const setUser = useSetAtom(currentUserAtom);

  const inFlightRef = useRef(false);

  useEffect(() => {
    const run = async () => {
      if (inFlightRef.current) return;

      inFlightRef.current = true;

      try {
        const me = await authService.getMe();

        setUser(me);

        // Re-afirmar el scope per-user en la extensión SIEMPRE que el bootstrap

        // considere autenticado (cookie o header), independientemente del carril

        // de re-login. Sin esto, un logout→login sin reiniciar la app que se

        // resuelve por cookie deja `currentUserId` en null en la extensión →

        // los snapshots per-user dan 204 → la app re-pide la escena (Welcome)

        // al seleccionar un workspace ya configurado. Idempotente / no-op sin

        // sesión Electron.

        await syncSessionWithExtension();

        setState("authenticated");
      } catch {
        setUser(null);

        setState("unauthenticated");
      } finally {
        inFlightRef.current = false;
      }
    };

    void run();

    const onAuthChanged = () => {
      // Reset to checking so guards show their spinner while we refetch.

      setState("checking");

      void run();
    };

    window.addEventListener("auth-changed", onAuthChanged);

    return () => window.removeEventListener("auth-changed", onAuthChanged);
  }, [setState, setUser]);
}
